/* istanbul ignore file */

import {
  AdCuePoint,
  AdObject,
  AdPluginSettings,
  AdStrings,
  AdType,
} from './ad-types';
import {
  triggerAdClicked,
  triggerAdEnded,
  triggerAdPlay,
  triggerAdSkipped,
} from './ad-event-triggers';

/**
 * Starts a linear ad by replacing the current source and making sure to
 * register the appropriate triggers to handle the lifecycle of the ad.
 * @param player The player instance
 * @param ad The ad object to play
 * @param settings Options passed to the ad plugin
 */
export function playVideoAd(
  player: any,
  source: AdType,
  adCue: AdCuePoint,
  ad: AdObject,
  settings: AdPluginSettings
) {
  if (!ad.videos) {
    return;
  }
  let startedTs = Date.now();
  player.ads.startLinearAdMode();
  let suppressAdsEvent = false;
  const thingsToCleanUp = [] as HTMLElement[];
  const triggersToDisconnect = [] as {
    type: string;
    fn: (...args: any[]) => any;
  }[];

  //#TODO: Select a video randomly
  const selectedVideo = ad.videos[0];

  player.src(selectedVideo.src);
  triggerAdPlay(player, source, adCue, ad, selectedVideo.fileName);

  // Helper functions
  const finishAd = () => {
    exitAd(player, thingsToCleanUp, triggersToDisconnect);
  };

  player.one('adprogress', () => {
    startedTs = Date.now();
    player.play();
  });

  player.one('adloadstart', () => {
    player.play();
  });

  // Inserts the clickable block on top of the ad.
  if (selectedVideo.href) {
    const adBlock = document.createElement('a');
    thingsToCleanUp.push(adBlock);
    adBlock.className = 'preroll-blocker';
    adBlock.href = selectedVideo.href;
    adBlock.target = ad.target || '_blank';
    adBlock.onclick = function () {
      if (!suppressAdsEvent) {
        triggerAdClicked(player, source, adCue, ad, selectedVideo.fileName);
        suppressAdsEvent = true;
      }
      player.pause();
    };
    player.el().insertBefore(adBlock, player.controlBar.el());
  }

  // Insert a time remaining div in case we don't allow skip is
  if (ad.showRemaining && !ad.allowSkip) {
    const remainingTime = document.createElement('div');
    remainingTime.className = 'remaining-time';
    thingsToCleanUp.push(remainingTime);
    player.el().appendChild(remainingTime);
    const timeRemaining = () => {
      updateRemainingTime(player, remainingTime, settings.strings);
    };
    triggersToDisconnect.push({ type: 'adtimeupdate', fn: timeRemaining });
    player.on('adtimeupdate', timeRemaining);
  }

  // Insert a skip button if needed
  let skippableNow = false;
  let skipButton: HTMLElement;
  if (ad.allowSkip !== false) {
    skipButton = document.createElement('div');
    skipButton.className = 'preroll-skip-button';
    player.el().appendChild(skipButton);
    thingsToCleanUp.push(skipButton);

    skipButton.onclick = function (e) {
      if (skippableNow) {
        if (!suppressAdsEvent) {
          triggerAdSkipped(
            player,
            'MIDROLL',
            adCue,
            ad,
            selectedVideo.fileName
          );
          suppressAdsEvent = true;
        }
        finishAd();
      }
      if (e.stopPropagation) {
        e.stopPropagation();
        return undefined;
      } else {
        return false;
      }
    };
    const timeRemaining = () => {
      skippableNow = updateTimeLeft(
        player,
        skipButton,
        ad.skipTime || 10,
        settings.strings
      );
    };
    triggersToDisconnect.push({ type: 'adtimeupdate', fn: timeRemaining });
    player.on('adtimeupdate', timeRemaining);
  }

  const onError = () => {
    finishAd();
  };

  const onAdEnd = () => {
    if (ensureAdPlayed(player, startedTs, skipButton, 1000, onAdEnd)) {
      if (!suppressAdsEvent) {
        triggerAdEnded(player, 'MIDROLL', adCue, ad, selectedVideo.fileName);
      }
      finishAd();
    }
  };

  player.one('error', onError);
  triggersToDisconnect.push({ type: 'error', fn: onError });
  player.one('adended', onAdEnd);
  triggersToDisconnect.push({ type: 'adended', fn: onAdEnd });
}

function updateRemainingTime(
  player: any,
  updateTimeDiv: HTMLElement,
  strings: AdStrings
) {
  player.loadingSpinner.hide();
  const timeLeft = Math.ceil(player.remainingTime());
  if (timeLeft > 0) {
    updateTimeDiv.innerHTML = strings['video start in'] + timeLeft;
  }
}

function updateTimeLeft(
  player: any,
  skipButton: HTMLElement,
  skipTime: number,
  strings: AdStrings
) {
  player.loadingSpinner.hide();
  let timeLeft = Math.ceil(skipTime - player.currentTime());
  if (timeLeft > 0) {
    skipButton.innerHTML = strings['skip in'] + timeLeft + '...';
    return false;
  } else {
    if (!skipButton.classList.contains('enabled')) {
      skipButton.classList.add('enabled');
      skipButton.innerHTML = strings.skip;
    }
    return true;
  }
}

function exitAd(
  player: videojs.VideoJsPlayer,
  thingsToCleanUp: HTMLElement[],
  triggersToDisconnect: { type: string; fn: (...a: any[]) => any }[]
) {
  thingsToCleanUp.forEach((el) => el.parentNode?.removeChild(el));
  triggersToDisconnect.forEach(({ type, fn }) => {
    player.off(type, fn);
  });

  player.loadingSpinner.show(); // Show Spinner to provide feedback of video loading status to user
  // @ts-ignore
  player.posterImage.hide(); // Hide Poster Image to provide feedback of video loading status to user
  player.bigPlayButton.hide(); // Hide Play Button to provide feedback of video loading status to user
  // @ts-ignore
  player.ads.endLinearAdMode();
}

function ensureAdPlayed(
  player: any,
  startedTs: number,
  skipButton: HTMLElement,
  playTimeThreshold: number,
  adEndTrigger: () => any
): boolean {
  if (Date.now() - startedTs > playTimeThreshold) {
    // All good, the ad played
    return true;
  }
  // The ad was stopped because of a race condition with live events, replay the ad and rewire
  // the trigger.
  player.play();
  player.one('adended', adEndTrigger);
  // Disable the skip button
  if (skipButton) {
    skipButton.classList.remove('enabled');
  }
  return false;
}
